import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { MotifIcon } from "@/components/MotifIcon";
import { NumberFormat } from "@/components/NumberFormat";
import { RouterLink } from "@/components/RouterLink";
import { Typography } from "@/components/Typography";
import { getFeatureLimitsNearCap } from "@/features/billing/billingAlerts";
import { useEnableBillingV2 } from "@/features/billing/useEnableBillingV2";
import { useEnableUcDashboard } from "@/features/billing/useEnableUcDashboard";
import { useLowBalance } from "@/features/billing/useLowBalance";
import {
  countChannelsNeedingAttention,
  NEEDS_ATTENTION_STATUSES,
} from "@/features/channels/channelAttention";
import { useOrgChannels } from "@/features/channels/useOrgChannels";
import { useEnableMetaAdsIntegration } from "@/features/meta-ads/useEnableMetaAdsIntegration";
import type { AttentionItem } from "@/features/platformStatus/attentionItems";
import { buildAttentionItems } from "@/features/platformStatus/attentionItems";
import { interlude } from "@/interlude";
import { ActiveContactsCard } from "@/pages/components/ActiveContactsCard";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";
import { SummaryCard, SummaryCardGrid } from "@/pages/components/SummaryCard";
import { DataCompleteness } from "@/pages/overview/DataCompleteness";

const styles = {
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  attentionList: {
    display: "flex",
    flexDirection: "column",
    margin: "0px",
    padding: "0px",
    listStyle: "none",
    border: `1px solid ${theme.colors.staticFgLine}`,
    borderRadius: "12px",
    backgroundColor: theme.colors.white,
  },
  attentionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "8px 16px",
    padding: "12px 20px",
    "&:not(:last-of-type)": {
      borderBottom: `1px solid ${theme.colors.staticFgLine}`,
    },
  },
  attentionText: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: theme.colors.staticFgTitle,
    "[un-i-motif]": {
      color: theme.colors.staticFgAttentive,
      fontSize: "1.25rem",
    },
  },
  allClear: {
    padding: "16px 20px",
    border: `1px solid ${theme.colors.staticFgLine}`,
    borderRadius: "12px",
    backgroundColor: theme.colors.white,
  },
  note: {
    color: theme.colors.staticFgNote,
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const AttentionRow: React.FC<{ item: AttentionItem }> = function AttentionRow({
  item,
}) {
  const { t } = useTranslation();
  const content: { text: string; link: React.ReactNode } = (() => {
    switch (item.kind) {
      case "channels":
        return {
          text: t("overview.attention.channels", {
            count: item.count,
            defaultValue: "{{count}} channels need attention",
          }),
          link: (
            <RouterLink to="/channels">
              {t("overview.attention.channels.action", "Review channels")}
            </RouterLink>
          ),
        };
      case "metaAds":
        return {
          text:
            item.tokenStatus === "expired"
              ? t(
                  "overview.attention.metaAds.expired",
                  "The Meta Ads connection has expired",
                )
              : t(
                  "overview.attention.metaAds.revoked",
                  "The Meta Ads connection was revoked",
                ),
          link: (
            <RouterLink to="/applications/meta-ads-integration">
              {t("overview.attention.metaAds.action", "Reconnect")}
            </RouterLink>
          ),
        };
      case "productFeeds":
        return {
          text: t("overview.attention.productFeeds", {
            count: item.count,
            defaultValue: "{{count}} product feeds failed to sync",
          }),
          link: (
            <RouterLink to="/applications/product-feeds">
              {t("overview.attention.productFeeds.action", "View feeds")}
            </RouterLink>
          ),
        };
      case "lowBalance":
        return {
          text: t(
            "overview.attention.lowBalance",
            "Your available balance is at or below your alert threshold",
          ),
          link: (
            <RouterLink to="/billing">
              {t("overview.attention.billing.action", "Go to billing")}
            </RouterLink>
          ),
        };
      case "featureLimits":
        return {
          text: t("overview.attention.featureLimits", {
            count: item.count,
            defaultValue: "{{count}} features have reached their usage alert",
          }),
          link: (
            <RouterLink to="/billing" search={{ tab: "alert-setting" }}>
              {t("overview.attention.billing.action", "Go to billing")}
            </RouterLink>
          ),
        };
    }
  })();

  return (
    <Box component="li" sx={styles.attentionRow}>
      <Box sx={styles.attentionText}>
        <MotifIcon un-i-motif="warning_filled" />
        <Typography variant="body">{content.text}</Typography>
      </Box>
      {content.link}
    </Box>
  );
};

/**
 * Everything on the platform that needs an admin, from data the org already
 * has. Signals that have not loaded, or do not apply to the org, add nothing;
 * the all-clear message waits until every signal has settled.
 */
const NeedsAttention: React.FC = function NeedsAttention() {
  const { t } = useTranslation();
  const orgChannels = useOrgChannels();
  const lowBalance = useLowBalance();
  const billingV2 = useEnableBillingV2();
  const featureLimitsQuery = interlude.billing.useGetFeatureLimits(undefined, {
    enabled: billingV2.isEnabled,
  });
  const enableMetaAds = useEnableMetaAdsIntegration();
  const metaAdsStatusQuery = interlude.metaAds.useGetStatus(undefined, {
    enabled: enableMetaAds,
  });
  const productFeedListQuery = interlude.productFeed.useListFeeds();

  const items = useMemo(
    () =>
      buildAttentionItems({
        channelsNeedingAttention: orgChannels.isSuccess
          ? countChannelsNeedingAttention(orgChannels.channels)
          : undefined,
        metaAdsTokenStatus: metaAdsStatusQuery.data?.tokenStatus,
        failedProductFeeds: productFeedListQuery.data?.feeds.filter(
          (feed) => feed.status === "error",
        ).length,
        lowBalance,
        featureLimitsNearCap: featureLimitsQuery.data
          ? getFeatureLimitsNearCap(featureLimitsQuery.data.featureLimits)
              .length
          : undefined,
      }),
    [
      featureLimitsQuery.data,
      lowBalance,
      metaAdsStatusQuery.data?.tokenStatus,
      orgChannels.channels,
      orgChannels.isSuccess,
      productFeedListQuery.data?.feeds,
    ],
  );

  // A disabled query stays pending in React Query, so only count the ones
  // that are actually running.
  const isSettling =
    orgChannels.isPending ||
    billingV2.isPending ||
    (billingV2.isEnabled && featureLimitsQuery.isPending) ||
    (enableMetaAds && metaAdsStatusQuery.isPending) ||
    productFeedListQuery.isPending;

  if (items.length === 0 && isSettling) return null;

  return (
    <Box component="section" sx={styles.section}>
      <Typography variant="h2">
        {t("overview.attention.title", "Needs attention")}
      </Typography>
      {items.length === 0 ? (
        <Box sx={styles.allClear}>
          <Typography variant="body">
            {t(
              "overview.attention.allClear",
              "Nothing needs attention right now.",
            )}
          </Typography>
        </Box>
      ) : (
        <Box component="ul" sx={styles.attentionList}>
          {items.map((item) => (
            <AttentionRow key={item.kind} item={item} />
          ))}
        </Box>
      )}
    </Box>
  );
};

const DataHubStatusCard: React.FC = function DataHubStatusCard() {
  const { t } = useTranslation();
  const unifyScopeSettingQuery =
    interlude.unifyScopeSetting.useGetUnifyScopeSetting();
  const isConnected = unifyScopeSettingQuery.data?.isCdhConnected ?? false;
  const mergeStateQuery = interlude.unifyScopeSetting.useGetUnifyMergeState(
    undefined,
    { enabled: isConnected },
  );

  if (!unifyScopeSettingQuery.isSuccess) return null;

  return (
    <SummaryCard
      title={t("dataHub.page.title", "Profile unification")}
      action={
        <RouterLink to="/data-hub">
          {isConnected
            ? t("common.manage", "Manage")
            : t("overview.dataHub.connect", "Set up")}
        </RouterLink>
      }
    >
      <Typography variant="body">
        {!isConnected
          ? t("dataHub.status.notConnected", "Not connected")
          : mergeStateQuery.isSuccess && !mergeStateQuery.data.isFinished
            ? t("dataHub.status.merging", "Merging profiles")
            : t("common.connected")}
      </Typography>
      {isConnected ? (
        <Typography variant="note" color={theme.colors.staticFgNote}>
          {unifyScopeSettingQuery.data.orgs
            .map((org) => org.source.toUpperCase())
            .join(" · ")}
        </Typography>
      ) : null}
    </SummaryCard>
  );
};

const ChannelsStatusCard: React.FC = function ChannelsStatusCard() {
  const { t } = useTranslation();
  const orgChannels = useOrgChannels();

  if (!orgChannels.isSuccess) return null;

  const total = orgChannels.channels.length;
  const needAttention = orgChannels.channels.filter((channel) =>
    NEEDS_ATTENTION_STATUSES.has(channel.status),
  ).length;

  return (
    <SummaryCard
      title={t("sideMenu.channels")}
      action={
        <RouterLink to="/channels">{t("common.manage", "Manage")}</RouterLink>
      }
    >
      <Typography variant="value">
        <NumberFormat value={total} />
      </Typography>
      <Typography variant="note" color={theme.colors.staticFgNote}>
        {needAttention > 0
          ? t("overview.channels.needAttention", {
              count: needAttention,
              defaultValue: "{{count}} need attention",
            })
          : t("overview.channels.allConnected", "All connected")}
      </Typography>
    </SummaryCard>
  );
};

const EcommerceStatusCard: React.FC = function EcommerceStatusCard() {
  const { t } = useTranslation();
  const hasEcModuleQuery = interlude.organization.useHasEcModule();
  const platforms = hasEcModuleQuery.data?.ecPlatforms;

  if (!platforms) return null;

  return (
    <SummaryCard
      title={t("dataHub.sources.ecommerce.title", "E-commerce platforms")}
      action={
        <RouterLink to="/applications">
          {t("common.manage", "Manage")}
        </RouterLink>
      }
    >
      <Typography variant="value">
        <NumberFormat value={platforms.length} />
      </Typography>
      <Typography variant="note" color={theme.colors.staticFgNote}>
        {t("overview.ecommerce.connected", "Connected through MAAC")}
      </Typography>
    </SummaryCard>
  );
};

const BalanceCard: React.FC = function BalanceCard() {
  const { t } = useTranslation();
  const balanceQuery = interlude.balance.useGetBalance();

  if (!balanceQuery.isSuccess) return null;

  return (
    <SummaryCard
      title={t("billing.availableBalance.label")}
      action={
        <RouterLink to="/billing">
          {t("overview.attention.billing.action", "Go to billing")}
        </RouterLink>
      }
    >
      <Typography variant="value">
        <NumberFormat value={balanceQuery.data.availableBalance} />
      </Typography>
    </SummaryCard>
  );
};

/**
 * The platform's home page: what needs attention, the state of Data Hub,
 * channels and integrations, and the org's key numbers. Every card is backed
 * by data the org already has and is left out until that data loads.
 */
export const OverviewPage: React.FC = function OverviewPage() {
  const { t } = useTranslation();
  const meQuery = interlude.auth.useGetMe();
  const billingV2 = useEnableBillingV2();
  const enableUcDashboard = useEnableUcDashboard();

  return (
    <PageLayout>
      <Stack gap="4px">
        <PageTitle>{t("overview.title", "Overview")}</PageTitle>
        {meQuery.data ? (
          <Box sx={styles.note}>
            <Typography variant="body">
              {meQuery.data.organization.name}
            </Typography>
          </Box>
        ) : null}
      </Stack>
      <NeedsAttention />
      <DataCompleteness />
      <Box component="section" sx={styles.section}>
        <Typography variant="h2">
          {t("overview.status.title", "Status")}
        </Typography>
        <SummaryCardGrid>
          <DataHubStatusCard />
          <ChannelsStatusCard />
          <EcommerceStatusCard />
          <BalanceCard />
          {billingV2.isEnabled && enableUcDashboard ? (
            <ActiveContactsCard />
          ) : null}
        </SummaryCardGrid>
      </Box>
    </PageLayout>
  );
};
